/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { DrmScheme, PlaybackStatistics, Player, TrackInfo, TrackType } from '@quickplay/player-interface';
import { Content, PlaybackType } from '../../../model/Content';
import { StreamType } from '../../../type/StreamType';
import { GodavariContentId, GodavariMetadataInput } from '../../reporter/model/GodavariPayload';

interface AudioVideoMetadata {
  containerFormat?: string;
  videoCodec?: string;
  audioCodec?: string;
  resolution?: string;
  audioLanguage?: string;
}

/** All measurements and browser data are captured by the caller before mapping. */
export interface GodavariPlayerMetadataSnapshot {
  content: Content;
  player: Partial<Pick<Player, 'sdkVersion' | 'currentTime' | 'duration' | 'isLive' | 'playbackRate'>>;
  statistics?: Partial<PlaybackStatistics>;
  audioVideoMetadata?: AudioVideoMetadata;
  activeTracks: readonly Pick<TrackInfo, 'type' | 'language'>[];
  streamUrl: string;
  streamingHost?: string;
  resourceTimings: readonly PerformanceEntry[];
  drmScheme: DrmScheme;
  contentId?: GodavariContentId;
  playbackSessionId?: string;
  playbackSessionStartTimeMs: number;
  startingBitrate?: number;
  normalizedWatchTime: number;
}

function contentFormat(streamUrl: string, containerFormat?: string): 'HLS' | 'DASH' | 'UNKNOWN' {
  const manifestPath = streamUrl.split(/[?#]/, 1)[0].toLowerCase();
  let inferredFormat = containerFormat?.toUpperCase();
  if (manifestPath.endsWith('.m3u8')) {
    inferredFormat = 'HLS';
  } else if (manifestPath.endsWith('.mpd')) {
    inferredFormat = 'DASH';
  }
  return inferredFormat === 'HLS' || inferredFormat === 'DASH' ? inferredFormat : 'UNKNOWN';
}

function dvrValue(playbackType: PlaybackType | undefined): 0 | 1 | undefined {
  if (playbackType === undefined) {
    return undefined;
  }
  return playbackType === PlaybackType.DVR ? 1 : 0;
}

function deliveryProtocol(streamUrl?: string): string {
  if(streamUrl === undefined || streamUrl === '') {
    return 'NA';
  }
  const protocol = /^([a-zA-Z][a-zA-Z\d+.-]*):/.exec(streamUrl)?.[1];
  if (protocol) return protocol.toLowerCase();
  // Protocol-relative or relative asset URIs (reported while ads own the player) still need a non-empty value.
  if (!streamUrl || typeof window === 'undefined') return '';
  try {
    return new URL(streamUrl, window.location.href).protocol.replace(/:$/, '').toLowerCase();
  } catch {
    return '';
  }
}

function timeToFirstByte(resourceTimings: readonly PerformanceEntry[]): number | undefined {
  for (const entry of resourceTimings) {
    if (
      entry.entryType !== 'resource' ||
      !('responseStart' in entry) ||
      !('requestStart' in entry) ||
      typeof entry.responseStart !== 'number' ||
      typeof entry.requestStart !== 'number' ||
      !Number.isFinite(entry.responseStart) ||
      !Number.isFinite(entry.requestStart) ||
      entry.requestStart <= 0 ||
      entry.responseStart < entry.requestStart
    ) {
      continue;
    }
    return Math.trunc(entry.responseStart - entry.requestStart);
  }
  return undefined;
}

function videoLength(content: Content, player: GodavariPlayerMetadataSnapshot['player']): number {
  if (player.isLive === true || content.playbackMode === StreamType.LIVE || content.isLive === StreamType.LIVE) {
    return 0;
  }
  for (const duration of [player.duration, content.duration]) {
    if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
      return duration;
    }
  }
  return 0;
}

/** Matches the in-market web player: connection.type (wifi, cellular) first, then effectiveType (4g, 3g), defaulting to 4g. */
function networkType(): string {
  const connection = (typeof navigator === 'undefined' ? undefined : navigator) as
    | { connection?: { type?: string; effectiveType?: string } }
    | undefined;
  return connection?.connection?.type || connection?.connection?.effectiveType || '4g';
}

/** Maps Quickplay measurements into Godavari units without reading or changing player state. */
export function toGodavariPlayerMetadata(snapshot: GodavariPlayerMetadataSnapshot): GodavariMetadataInput {
  const { content, player, audioVideoMetadata, activeTracks, streamUrl } = snapshot;
  const firstByte = timeToFirstByte(snapshot.resourceTimings);
  const protocol = deliveryProtocol(streamUrl);

  return {
    // Godavari video session ids are dash-free.
    video_session_id: snapshot.playbackSessionId?.replace(/-/g, ''),
    video_session_start_time: snapshot.playbackSessionStartTimeMs,
    video_content_id: snapshot.contentId,
    video_streaming_mode: 'streaming',
    video_network_type: networkType(),
    video_video_type: content.playbackMode === StreamType.LIVE || content.isLive === StreamType.LIVE ? 'live' : 'vod',
    video_dvr: dvrValue(content.playbackType),
    video_player_name: 'Quickplay Player',
    video_player_version: player.sdkVersion,
    video_content_format: contentFormat(streamUrl, audioVideoMetadata?.containerFormat),
    video_drm: snapshot.drmScheme === DrmScheme.NONE ? 'clear' : snapshot.drmScheme,
    video_codec: audioVideoMetadata?.videoCodec || 'NA',
    selected_audio_codec: audioVideoMetadata?.audioCodec,
    selected_video_codec: audioVideoMetadata?.videoCodec,
    video_resolution: audioVideoMetadata?.resolution?.replace('x', '*'),
    video_audio_language:
      activeTracks.find(track => track.type === TrackType.AUDIO)?.language ||
      audioVideoMetadata?.audioLanguage ||
      content.contentLanguage,
    video_subtitle_language: activeTracks.find(track => track.type === TrackType.TEXT)?.language || 'off',
    video_streaming_host: snapshot.streamingHost ?? 'NA',
    ...(snapshot.streamingHost ? { video_cdn: snapshot.streamingHost } : {}),
    // An empty value would overwrite the last known protocol and fail the required vs-dp check.
    ...(protocol ? { video_delivery_protocol: protocol } : {}),
    ...(firstByte === undefined ? {} : { video_time_to_first_byte: String(firstByte) }),
    ...toGodavariProgressMetadata(snapshot),
    ...(content.packageName ? { subscribed_pack_name: content.packageName } : {}),
    ...(content.contractedResolution ? { subscribed_pack_resolution: content.contractedResolution } : {}),
    custom_tags: content.customMetadata,
  };
}

/** Frequent measurements; excludes track, URL, resource timing, and content mapping work. */
export function toGodavariProgressMetadata(
  snapshot: Pick<GodavariPlayerMetadataSnapshot, 'content' | 'player' | 'statistics' | 'startingBitrate' | 'normalizedWatchTime'>,
): GodavariMetadataInput {
  const { player, statistics: stats } = snapshot;
  const bufferAhead = stats?.bufferedAhead;
  const bufferAvailableInMilliseconds =
    typeof bufferAhead === 'number' && Number.isFinite(bufferAhead) && bufferAhead >= 0
      ? Math.trunc(bufferAhead * 1000)
      : undefined;
  return {
    video_length: videoLength(snapshot.content, player),
    video_position: String(Math.ceil((player.currentTime ?? 0) * 1000)),
    video_starting_bitrate: snapshot.startingBitrate === undefined ? undefined : String(snapshot.startingBitrate),
    video_current_bitrate: (stats?.variantBitrate ?? 0) / 1000,
    buffer_available_in_milliseconds: bufferAvailableInMilliseconds,
    estimated_bandwidth_in_kbbs: (stats?.estimatedBandwidth ?? 0) / 1000,
    video_playing_rate: `${player.playbackRate ?? 1}x`,
    video_session_watch_time_duration: stats?.playTime ?? 0,
    normalized_watch_time: snapshot.normalizedWatchTime,
    video_increment_buffer_time_duration: stats?.bufferingTime ?? 0,
    video_dropped_frames: stats?.droppedFrames,
    event_current_playback_rate: player.playbackRate ?? 1,
  };
}
